import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { WorkingDayKey } from '../WorkingDays/WorkingDays.types'
import WorkingDays from '../WorkingDays/WorkingDays.component'
import type {
  DayTimeParts,
  DayTimePeriod,
  DayTimeSelectorProps,
  DayTimeSelectorValue,
} from './DayTimeSelector.types'
import {
  DAY_TIME_SELECTOR_DEMO_VALUE,
  DAY_TIME_SELECTOR_PERIODS,
  EMPTY_DAY_TIME_SELECTOR_VALUE,
} from './DayTimeSelector.types'
import {
  clampTimeDigit,
  formatDayTimeSelectorValue,
  normalizeMinuteOnBlur,
  truncateDayTimeSummary,
} from './DayTimeSelector.utils'
import './DayTimeSelector.css'

const createEmptyParts = (period: DayTimePeriod = 'AM'): DayTimeParts => ({
  hour: '',
  minute: '',
  period,
})

const TimeSegmentInputs: React.FC<{
  value: DayTimeParts | null
  disabled?: boolean
  darkMode?: boolean
  ariaLabel: string
  onChange: (next: DayTimeParts) => void
}> = ({ value, disabled = false, darkMode = false, ariaLabel, onChange }) => {
  const parts = value ?? createEmptyParts()

  const update = (patch: Partial<DayTimeParts>): void => {
    onChange({ ...parts, ...patch })
  }

  return (
    <div
      className={`ds-day-time-selector__time${
        darkMode ? ' ds-day-time-selector__time--dark' : ''
      }`}
      role="group"
      aria-label={ariaLabel}
    >
      <input
        type="text"
        className="ds-day-time-selector__digit"
        inputMode="numeric"
        maxLength={2}
        placeholder="0"
        aria-label={`${ariaLabel} hour`}
        disabled={disabled}
        value={parts.hour}
        onChange={(event) =>
          update({ hour: clampTimeDigit('hour', event.target.value) })
        }
      />
      <span className="ds-day-time-selector__colon" aria-hidden="true">
        :
      </span>
      <input
        type="text"
        className="ds-day-time-selector__digit"
        inputMode="numeric"
        maxLength={2}
        placeholder="00"
        aria-label={`${ariaLabel} minute`}
        disabled={disabled}
        value={parts.minute}
        onChange={(event) =>
          update({ minute: clampTimeDigit('minute', event.target.value) })
        }
        onBlur={() => update({ minute: normalizeMinuteOnBlur(parts.minute) })}
      />
      <select
        className="ds-day-time-selector__period"
        aria-label={`${ariaLabel} period`}
        disabled={disabled}
        value={parts.period}
        onChange={(event) =>
          update({ period: event.target.value as DayTimePeriod })
        }
      >
        {DAY_TIME_SELECTOR_PERIODS.map((period) => (
          <option key={period} value={period}>
            {period}
          </option>
        ))}
      </select>
    </div>
  )
}

const DayTimeSelector: React.FC<DayTimeSelectorProps> = ({
  label = 'Preferred appointment times',
  placeholder = 'Select',
  value,
  defaultValue = EMPTY_DAY_TIME_SELECTOR_VALUE,
  disabled = false,
  darkMode = false,
  className = '',
  previewState,
  daysLabel = 'Preferred days',
  startLabel = 'Between',
  endLabel = 'Till',
  onChange,
  onOpenChange,
}) => {
  const triggerId = useId()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [internalValue, setInternalValue] =
    useState<DayTimeSelectorValue>(defaultValue)
  const [menuOpen, setMenuOpen] = useState(false)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isControlled = value !== undefined

  const resolvedValue = useMemo((): DayTimeSelectorValue => {
    if (isPreview) {
      if (previewState === 'default') {
        return EMPTY_DAY_TIME_SELECTOR_VALUE
      }
      return DAY_TIME_SELECTOR_DEMO_VALUE
    }
    return isControlled ? (value as DayTimeSelectorValue) : internalValue
  }, [internalValue, isControlled, isPreview, previewState, value])

  const isMenuVisible =
    isPreview && previewState === 'open'
      ? true
      : !isPreview && menuOpen && !isDisabled

  const summary = formatDayTimeSelectorValue(resolvedValue)
  const displayText = summary ? truncateDayTimeSummary(summary) : placeholder
  const hasValue = Boolean(summary)

  const emitChange = useCallback(
    (next: DayTimeSelectorValue): void => {
      if (!isControlled) {
        setInternalValue(next)
      }
      onChange?.(next)
    },
    [isControlled, onChange],
  )

  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (isPreview || isDisabled) {
        return
      }
      setMenuOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [isDisabled, isPreview, onOpenChange],
  )

  useEffect(() => {
    if (!isMenuVisible || isPreview) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMenuVisible, isPreview, setOpen])

  const handleDaysChange = (days: WorkingDayKey[]): void => {
    emitChange({ ...resolvedValue, days })
  }

  const handleStartChange = (start: DayTimeParts): void => {
    emitChange({ ...resolvedValue, start })
  }

  const handleEndChange = (end: DayTimeParts): void => {
    emitChange({ ...resolvedValue, end })
  }

  const classNames = [
    'ds-day-time-selector',
    darkMode ? 'ds-day-time-selector--dark' : '',
    isDisabled ? 'ds-day-time-selector--disabled' : '',
    isMenuVisible ? 'ds-day-time-selector--open' : '',
    hasValue ? 'ds-day-time-selector--filled' : '',
    isPreview ? 'ds-day-time-selector--preview' : '',
    previewState ? `ds-day-time-selector--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={rootRef} className={classNames}>
      {label ? (
        <label className="ds-day-time-selector__label" htmlFor={triggerId}>
          {label}
        </label>
      ) : null}

      <div className="ds-day-time-selector__field">
        <div className="ds-day-time-selector__control">
          <button
            id={triggerId}
            type="button"
            className="ds-day-time-selector__trigger"
            aria-haspopup="dialog"
            aria-expanded={isMenuVisible}
            aria-controls={panelId}
            disabled={isDisabled}
            onClick={() => setOpen(!isMenuVisible)}
          >
            <span
              className={`ds-day-time-selector__value${
                hasValue ? '' : ' ds-day-time-selector__value--placeholder'
              }`}
            >
              {displayText}
            </span>
            <i
              className="mgc mgc_down_small_fill ds-day-time-selector__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isMenuVisible ? (
          <div
            id={panelId}
            className="ds-day-time-selector__panel"
            role="dialog"
            aria-label={label || 'Day and time selector'}
          >
            <div className="ds-day-time-selector__section">
              <p className="ds-day-time-selector__section-label">{daysLabel}</p>
              <WorkingDays
                className="ds-day-time-selector__days"
                value={resolvedValue.days}
                interactive={!isDisabled && !isPreview}
                darkMode={darkMode}
                onChange={handleDaysChange}
              />
            </div>

            <div className="ds-day-time-selector__time-headers">
              <span>{startLabel}</span>
              <span>{endLabel}</span>
            </div>

            <div className="ds-day-time-selector__time-row">
              <TimeSegmentInputs
                value={resolvedValue.start}
                disabled={isDisabled || isPreview}
                darkMode={darkMode}
                ariaLabel={startLabel}
                onChange={handleStartChange}
              />
              <span
                className="ds-day-time-selector__range-sep"
                aria-hidden="true"
              >
                -
              </span>
              <TimeSegmentInputs
                value={resolvedValue.end}
                disabled={isDisabled || isPreview}
                darkMode={darkMode}
                ariaLabel={endLabel}
                onChange={handleEndChange}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default DayTimeSelector
